import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import logo from "@/assets/rkd-logo.png";
import { openBooking } from "./openBooking";

type NavItem = { label: string; to?: string; href?: string };
const navItems: NavItem[] = [
  { label: "Services", to: "/services" },
  { label: "Gallery", to: "/gallery" },
  { label: "Before & After", to: "/before-after" },
  { label: "Reviews", href: "/#reviews" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", to: "/contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 bg-background transition-shadow ${
        scrolled ? "shadow-[0_1px_0_0_rgba(0,0,0,0.06)]" : ""
      }`}
    >
      {/* Slim utility strip */}
      <div className="bg-primary text-primary-foreground text-[11px]">
        <div className="mx-auto max-w-7xl px-5 md:px-8 flex items-center justify-between py-1.5">
          <span className="tracking-wide">
            Serving Park City · Salt Lake · Utah County · Since 1996
          </span>
          <span className="hidden md:inline tracking-[0.2em] uppercase text-primary-foreground font-semibold">
            BBB A+ · Builder's Choice 3×
          </span>
        </div>
      </div>

      {/* Main bar — bold logo left, oversized phone right */}
      <div className="border-b border-border">
        <div className="mx-auto max-w-7xl px-5 md:px-8 flex items-center justify-between gap-6 py-4 md:py-5">
          {/* LEFT: Logo */}
          <Link to="/" className="flex items-center gap-4 shrink-0 group">
            <img
              src={logo}
              alt="RKD Tile logo"
              width={64}
              height={64}
              className="h-14 md:h-16 w-auto"
              fetchPriority="low"
            />
            <div className="leading-none">
              <div className="font-display text-2xl md:text-[28px] font-semibold text-foreground tracking-tight">
                RKD Tile
              </div>
              <div className="mt-1.5 text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-muted-foreground">
                Custom Tile · Stone · Flooring
              </div>
            </div>
          </Link>

          {/* RIGHT: Refined phone CTA */}
          <a
            href="tel:8015982226"
            className="hidden md:flex items-center gap-3 group"
          >
            <div className="text-right leading-tight">
              <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                Call for a free estimate
              </div>
              <div className="font-display text-xl lg:text-2xl font-semibold text-primary group-hover:text-accent transition-colors tracking-tight">
                (801) 598-2226
              </div>
            </div>
            <div className="h-10 w-10 lg:h-11 lg:w-11 rounded-full bg-accent text-accent-foreground flex items-center justify-center group-hover:scale-105 transition-transform shadow-[0_6px_18px_-10px_rgba(0,0,0,0.35)]">
              <Phone className="h-4 w-4 lg:h-5 lg:w-5" strokeWidth={2.25} />
            </div>
          </a>

          {/* Mobile: compact phone + menu */}
          <div className="md:hidden flex items-center gap-2">
            <a
              href="tel:8015982226"
              className="h-11 w-11 rounded-full bg-accent text-accent-foreground flex items-center justify-center"
              aria-label="Call RKD Tile"
            >
              <Phone className="h-5 w-5" />
            </a>
            <button
              className="h-11 w-11 flex items-center justify-center text-foreground"
              onClick={() => setOpen((o) => !o)}
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Bottom nav row */}
        <div className="hidden md:block border-t border-border/70">
          <div className="mx-auto max-w-7xl px-5 md:px-8 flex items-center justify-between">
            <nav className="flex items-center gap-10 text-sm font-medium">
              {navItems.map((item) => {
                const cls =
                  "relative py-3.5 text-foreground/85 hover:text-primary transition-colors after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[2px] after:bg-accent after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:origin-left";
                return item.to ? (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={cls}
                    activeProps={{ className: `${cls} text-primary` }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a key={item.label} href={item.href} className={cls}>
                    {item.label}
                  </a>
                );
              })}
            </nav>
            <button
              type="button"
              onClick={openBooking}
              className="inline-flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-xs font-semibold tracking-[0.18em] uppercase hover:bg-primary transition-colors"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav className="md:hidden border-b border-border bg-background">
          <div className="mx-auto max-w-7xl px-5 py-2">
            {navItems.map((item) => {
              const cls =
                "block py-3 text-sm font-medium text-foreground/85 border-b border-border last:border-b-0";
              return item.to ? (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={cls}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className={cls}
                >
                  {item.label}
                </a>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openBooking();
              }}
              className="block w-full mt-3 mb-3 bg-foreground text-background text-center py-3 text-xs font-semibold tracking-[0.18em] uppercase"
            >
              Schedule Consultation
            </button>
          </div>
        </nav>
      )}
    </header>
  );
}
